import { useState, useCallback, useEffect, useRef } from "react";
import { customerService } from "../services";
import toast from "react-hot-toast";

export const useCustomers = (params = {}, user = null) => {
  // State management
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);

  // Loading states
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isToggling, setIsToggling] = useState(false);

  // Refs to avoid stale closures — fetchCustomers reads these instead of closing over state
  const pageRef = useRef(1);
  const searchRef = useRef("");
  const lastRequestTimeRef = useRef(0);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const THROTTLE_DELAY = 1000;

  // Stable fetch function — deps are only user/params, not page/search
  // This prevents the search debounce effect from re-firing on every page change
  const fetchCustomers = useCallback(async () => {
    const now = Date.now();
    if (now - lastRequestTimeRef.current < THROTTLE_DELAY) return;
    lastRequestTimeRef.current = now;

    setIsLoading(true);
    try {
      const apiParams = {
        page: pageRef.current,
        limit: 10,
        search: searchRef.current,
        ...params,
      };

      if (user?.role?.id === 2 && user?.company?.id) {
        apiParams.companyId = user.company.id;
      }

      const response = await customerService.getAll(apiParams);
      setCustomers(response.data.customers || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setTotalItems(response.data.pagination?.total || 0);
      retryCountRef.current = 0;
    } catch (error) {
      if (error.response?.status === 429) {
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++;
          const retryDelay = Math.pow(2, retryCountRef.current) * 1000;
          toast.error(
            `Rate limited. Retrying in ${retryDelay / 1000} seconds...`,
          );
          setTimeout(() => fetchCustomers(), retryDelay);
          return;
        } else {
          toast.error("Too many requests. Please try again later.");
        }
      } else {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to fetch customers",
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [user, params]); // stable — does NOT depend on currentPage or searchTerm

  // Search debounce — only fires when searchTerm changes
  // fetchCustomers is stable, so this effect won't retrigger on page changes
  useEffect(() => {
    searchRef.current = searchTerm;
    const timeoutId = setTimeout(() => {
      pageRef.current = 1;
      setCurrentPage(currentPage);
      fetchCustomers();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchCustomers]);

  // Page change — fetch immediately when page changes
  useEffect(() => {
    pageRef.current = currentPage;
    fetchCustomers();
  }, [currentPage, fetchCustomers]);

  // Create customer
  const createCustomer = useCallback(
    async (customerData) => {
      setIsCreating(true);
      try {
        await customerService.create(customerData);
        toast.success("Customer created successfully!");
        setShowModal(false);
        setEditingCustomer(null);
        await fetchCustomers();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to create customer",
        );
      } finally {
        setIsCreating(false);
      }
    },
    [fetchCustomers],
  );

  // Update customer
  const updateCustomer = useCallback(
    async (id, customerData) => {
      setIsUpdating(true);
      try {
        await customerService.update(id, customerData);
        toast.success("Customer updated successfully!");
        setShowModal(false);
        setEditingCustomer(null);
        await fetchCustomers();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update customer",
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [fetchCustomers],
  );

  // Delete customer
  const deleteCustomer = useCallback(
    async (id) => {
      setIsDeleting(true);
      try {
        await customerService.delete(id);
        toast.success("Customer deleted successfully!");
        await fetchCustomers();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to delete customer",
        );
      } finally {
        setIsDeleting(false);
      }
    },
    [fetchCustomers],
  );

  // Toggle status
  const toggleStatus = useCallback(
    async (id) => {
      setIsToggling(true);
      try {
        await customerService.toggleStatus(id);
        toast.success("Customer status updated successfully!");
        await fetchCustomers();
      } catch (error) {
        toast.error(
          error.response?.data?.message ||
            error.message ||
            "Failed to update customer status",
        );
      } finally {
        setIsToggling(false);
      }
    },
    [fetchCustomers],
  );

  // Handlers
  const handleEdit = useCallback((customer) => {
    setEditingCustomer(customer);
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(
    (customer) => {
      if (window.confirm("Are you sure you want to delete this customer?")) {
        deleteCustomer(customer.id);
      }
    },
    [deleteCustomer],
  );

  const handleToggleStatus = useCallback(
    (customer) => {
      toggleStatus(customer.id);
    },
    [toggleStatus],
  );

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingCustomer(null);
  }, []);

  const handleSubmit = useCallback(
    async (data) => {
      console.log("useCustomers - handleSubmit called with data:", data);
      console.log("useCustomers - editingCustomer:", editingCustomer);

      // Use editingCustomer.id to determine if it's an update or create
      if (editingCustomer?.id) {
        console.log(
          "useCustomers - Calling updateCustomer with ID:",
          editingCustomer.id,
        );
        await updateCustomer(editingCustomer.id, data);
      } else {
        console.log("useCustomers - Calling createCustomer");
        await createCustomer(data);
      }
    },
    [createCustomer, updateCustomer, editingCustomer],
  );

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setCurrentPage(1);
  }, []);

  const handleRetry = useCallback(() => {
    retryCountRef.current = 0;
    fetchCustomers();
  }, [fetchCustomers]);

  const refreshCustomers = useCallback(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  return {
    // Data
    customers,
    isLoading,
    currentPage,
    totalPages,
    totalItems,

    // Loading states
    isCreating,
    isUpdating,
    isDeleting,
    isToggling,

    // Modal state
    showModal,
    editingCustomer,

    // Handlers
    handleSubmit,
    handleEdit,
    handleDelete,
    handleToggleStatus,
    handleCloseModal,
    handlePageChange,
    handleClearFilters,
    handleRetry,

    // Direct functions
    createCustomer,
    updateCustomer,
    deleteCustomer,
    toggleStatus,
    fetchCustomers,
    refreshCustomers,

    // Search
    searchTerm,
    setSearchTerm,
  };
};

export default useCustomers;
